import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import participationsService from '../services/participationsService';

/**
 * Composant bouton pour gérer la participation à un tournoi
 */
const ParticipationButton = ({ tournamentId, onParticipationChange, registrationDisabled = false }) => {
  const [isParticipating, setIsParticipating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Vérifier la participation au chargement
  useEffect(() => {
    checkParticipation();
  }, [tournamentId]);

  const checkParticipation = async () => {
    try {
      setIsLoading(true);
      const participating = await participationsService.checkTournamentParticipation(tournamentId);
      setIsParticipating(participating);
    } catch (err) {
      console.error('Erreur vérification participation:', err);
      setError('Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleParticipation = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (isParticipating) {
        // ✅ IMPLÉMENTÉ : Désinscription
        await participationsService.unregisterFromTournament(tournamentId);
        setIsParticipating(false);

        // Notifier le parent du changement
        if (onParticipationChange) {
          onParticipationChange(false);
        }
      } else {
        // S'inscrire
        await participationsService.registerToTournament(tournamentId);
        setIsParticipating(true);

        // Notifier le parent du changement
        if (onParticipationChange) {
          onParticipationChange(true);
        }
      }
    } catch (err) {
      console.error('Erreur changement participation:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <button disabled className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed">
        <Loader size={18} className="animate-spin" />
        Chargement...
      </button>
    );
  }

  if (error && !isParticipating) {
    return (
      <button
        onClick={checkParticipation}
        className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
      >
        <XCircle size={18} />
        Réessayer
      </button>
    );
  }

  // Day sans inscription propre (ex. Final Day atteint via un day précédent) :
  // pas de nouvelle inscription possible. Un joueur déjà inscrit (donnée
  // historique) garde le bouton pour pouvoir se désinscrire.
  if (registrationDisabled && !isParticipating) {
    return (
      <button
        disabled
        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed"
        title="Ce day n'a pas d'inscription propre : on y accède via un day précédent"
      >
        Pas d'inscription directe
      </button>
    );
  }

  return (
    <div>
      <button
        onClick={handleToggleParticipation}
        disabled={isSubmitting}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
          isParticipating
            ? 'bg-green-600 text-white hover:bg-red-600 hover:shadow-lg'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={isParticipating ? 'Cliquez pour vous désinscrire' : 'Cliquez pour vous inscrire'}
      >
        {isSubmitting ? (
          <>
            <Loader size={18} className="animate-spin" />
            Chargement...
          </>
        ) : isParticipating ? (
          <>
            <CheckCircle size={18} />
            <span className="hidden sm:inline">✓ Inscrit</span>
            <span className="sm:hidden">✓</span>
          </>
        ) : (
          <>
            📝 Je participe
          </>
        )}
      </button>
      {error && (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
};

export default ParticipationButton;